import React, { useContext, useState } from 'react';
import { MobileCredentialPage } from './MobileFirstScreen';
import { DesktopCredentialPage } from './DesktopFirstScreen';
import { LabelsContext } from 'helpers/contexts';
import { createUser } from 'Services/Api';
import Cookies from 'js-cookie';
import { hasInvalidChars } from 'helpers/utilities';
import { getLanguageFromQueryParams } from 'languages';
import * as Constant from 'constant';

function FirstScreen(props) {
  const { signUp, global } = useContext(LabelsContext);
  const [regId, setRegId] = useState('');
  const [emailInputValue, setEmailInputValue] = useState('');
  const [fullName, setFullName] = useState('');
  const [phoneNumberInputValue, setPhoneNumberInputValue] = useState('');
  const [passwordInputValue, setPasswordInputValue] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [userExists, setUserExists] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [fullNameError, setFullNameError] = useState('');
  const shouldShowPasswordError = passwordError.length > 0;

  const handleLoginClick = () => {
    window.location = 'https://app.fireberry.com';
  };

  const validateEmail = () => {
    if (!emailInputValue) {
      setEmailError(global.requiredField);
      return false;
    }

    const isEmailValue = Constant.EMAIL_ADDRESS_REGEX.test(String(emailInputValue).toLowerCase());
    if (!isEmailValue) {
      setEmailError(signUp.credentialsPage.emailError.invalid);
      return false;
    }

    if (hasInvalidChars(emailInputValue)) {
      setEmailError(signUp.removeSpecialChars);
      return false;
    }

    return true;
  };

  const getUtmsFromLocalStorage = () => {
    const utmsObj = {};
    Constant.UTM_PARAMETERS.forEach((utm) => {
      const utmValue = localStorage.getItem(utm.parameterName);
      if (utmValue) {
        utmsObj[utm.fieldName] = utmValue;
      } else {
        utmsObj[utm.fieldName] = '';
      }
    });

    return utmsObj;
  };

  const getUtmsFromCookies = () => {
    const utmsObj = {};
    Constant.UTM_COOKIES_PARAMETERS.forEach((utm) => {
      const utmValue = Cookies.get(utm.cookieName);
      if (utmValue) {
        utmsObj[utm.fieldName] = utmValue;
      } else {
        utmsObj[utm.fieldName] = '';
      }
    });

    return utmsObj;
  };

  const isValidPasswordLength = () => {
    const isValid = passwordInputValue.length >= VALID_PASSWORD_LENGTH;

    if (!isValid) {
      setPasswordError(signUp.credentialsPage.passwordError.length);
    }
    return isValid;
  };

  const validateFullName = () => {
    if (fullName === '') {
      setFullNameError(global.requiredField);
      return false;
    }

    if (hasInvalidChars(fullName)) {
      setFullNameError(signUp.removeSpecialChars);
      return false;
    }
    return true;
  };

  const validatePassword = () => {
    if (passwordInputValue) {
      return isValidPasswordLength();
    }

    setPasswordError(global.requiredField);
    return false;
  };

  const validatePhoneNumber = () => {
    if (phoneNumberInputValue.length === 0) {
      setPhoneNumberError(global.requiredField);
      return false;
    }

    if (hasInvalidChars(phoneNumberInputValue)) {
      setPhoneNumberError(signUp.removeSpecialChars);
      return false;
    }
    return true;
  };

  const handleCredentialsSubmit = async () => {
    const isEmailValid = validateEmail();
    const isPhoneNumberValid = validatePhoneNumber();
    const isPasswordValid = validatePassword();
    const isFullNameValid = validateFullName();
    const language = getLanguageFromQueryParams();

    if (isEmailValid && isPhoneNumberValid && isPasswordValid && isFullNameValid) {
      setCreatingUser(true);
      const utms = getUtmsFromLocalStorage();
      const cookieUtms = getUtmsFromCookies();
      const response = await createUser(emailInputValue.trim(), passwordInputValue, language, phoneNumberInputValue, fullName.trim(), regId, {
        ...utms,
        ...cookieUtms,
      }).finally(() => {
        setCreatingUser(false);
      });

      if (response.success) {
        setRegId(response.registrationNumber);
        props.onSuccess && props.onSuccess(response.registrationNumber);
      } else if (response.message.toLowerCase() === USERNAME_EXISTS) {
        setUserExists(true);
        setEmailError(signUp.credentialsPage.emailError.exist);
      }
    }
  };

  const handleEmailInputChange = (value) => {
    if (emailError.length > 0) {
      setEmailError('');
    }

    if (userExists) {
      setUserExists(false);
    }

    setEmailInputValue(value);
  };

  const handlePasswordInputChange = (value) => {
    if (passwordError.length > 0) {
      setPasswordError('');
    }
    setPasswordInputValue(value);
  };

  const handlePhoneNumberInputChange = (value) => {
    setPhoneNumberError('');
    setPhoneNumberInputValue(value);
  };

  const handleFullNameChange = (value) => {
    setFullNameError('');
    setFullName(value);
  };

  const sharedProps = {
    onEmailChange: handleEmailInputChange,
    onPhoneNumberChange: handlePhoneNumberInputChange,
    onFullNameChange: handleFullNameChange,
    onSubmit: handleCredentialsSubmit,
    onLoginClick: handleLoginClick,
    isRtl: props.isRtl,
    emailValue: emailInputValue,
    phoneNumberValue: phoneNumberInputValue,
    phoneNumberError: phoneNumberError,
    emailErr: emailError,
    fullName,
    fullNameError: fullNameError,
    isLoading: creatingUser,
    onPasswordChange: handlePasswordInputChange,
    passwordValue: passwordInputValue,
    showPasswordError: shouldShowPasswordError,
    passwordErr: passwordError,
  };

  return props.isMobileMode || props.isTabletBreakpoint ? <MobileCredentialPage {...sharedProps} /> : <DesktopCredentialPage {...sharedProps} />;
}

const USERNAME_EXISTS = 'username exists';
const VALID_PASSWORD_LENGTH = 5;

export default FirstScreen;
